import { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { compressImage } from '@/lib/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '',
  image_url: '',
  product_info: '',
  ml_url: '',
  category: '',
  unit_price: '',
  medio_mayoreo_qty: '',
  medio_mayoreo_price: '',
  mayoreo_qty: '',
  mayoreo_price: '',
  shipment_qty: '',
  shipment_price: '',
  visible: true,
};

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

/** Aviso al admin cuando dentro de una categoría hay productos con umbrales
 * (medio_mayoreo_qty / mayoreo_qty) distintos. No corrompe nada — cada
 * producto sigue respetando su propio umbral — pero conviene alinearlos
 * para que la promesa "combina sabores" funcione consistente.
 */
function CategoryConsistencyWarning({ items }) {
  const issues = [];
  const groups = {};
  (items || []).forEach((p) => {
    if (!p.category) return;
    (groups[p.category] = groups[p.category] || []).push(p);
  });
  for (const [cat, list] of Object.entries(groups)) {
    if (list.length < 2) continue;
    const mms = new Set(list.map((x) => x.medio_mayoreo_qty || null).filter((v) => v));
    const mms_p = new Set(list.map((x) => x.medio_mayoreo_price || null).filter((v) => v));
    const ms = new Set(list.map((x) => x.mayoreo_qty || null).filter((v) => v));
    const ms_p = new Set(list.map((x) => x.mayoreo_price || null).filter((v) => v));
    const badges = [];
    if (mms.size > 1) badges.push(`medio mayoreo qty: ${[...mms].join(' / ')}`);
    if (mms_p.size > 1) badges.push(`medio mayoreo precio: ${[...mms_p].map(money).join(' / ')}`);
    if (ms.size > 1) badges.push(`mayoreo qty: ${[...ms].join(' / ')}`);
    if (ms_p.size > 1) badges.push(`mayoreo precio: ${[...ms_p].map(money).join(' / ')}`);
    if (badges.length > 0) issues.push({ cat, badges });
  }
  if (issues.length === 0) return null;
  return (
    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
      <p className="font-semibold mb-1">⚠️ Umbrales distintos dentro de una misma categoría</p>
      <p className="mb-2">
        No pasa nada — cada producto respeta su propio umbral — pero para que la promesa "combina sabores"
        sea consistente, conviene alinearlos:
      </p>
      <ul className="ml-3 list-disc space-y-0.5">
        {issues.map((i) => (
          <li key={i.cat}><b>{i.cat}</b>: {i.badges.join(' · ')}</li>
        ))}
      </ul>
    </div>
  );
}

export const KoreanProductsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/korean-products`, { withCredentials: true });
      setItems(data || []);
    } catch (e) {
      toast.error('No se pudieron cargar los productos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const openDialog = (item = null) => {
    setEditing(item);
    if (item) {
      setForm({
        name: item.name || '',
        image_url: item.image_url || '',
        product_info: item.product_info || '',
        ml_url: item.ml_url || '',
        category: item.category || '',
        unit_price: item.unit_price ?? '',
        medio_mayoreo_qty: item.medio_mayoreo_qty ?? '',
        medio_mayoreo_price: item.medio_mayoreo_price ?? '',
        mayoreo_qty: item.mayoreo_qty ?? '',
        mayoreo_price: item.mayoreo_price ?? '',
        shipment_qty: item.shipment_qty ?? '',
        shipment_price: item.shipment_price ?? '',
        visible: item.visible !== false,
      });
    } else {
      setForm(EMPTY);
    }
    setShowDialog(true);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const dataUrl = await compressImage(file);
      setForm((f) => ({ ...f, image_url: dataUrl }));
    } catch (err) {
      toast.error(err?.message || 'No se pudo procesar la imagen');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nombre requerido'); return; }
    const unit = Number(form.unit_price);
    if (!(unit > 0)) { toast.error('Precio unitario requerido'); return; }

    const payload = {
      name: form.name.trim(),
      image_url: form.image_url || null,
      product_info: form.product_info || null,
      ml_url: (form.ml_url || '').trim() || null,
      category: (form.category || '').trim() || null,
      unit_price: unit,
      visible: !!form.visible,
    };
    const mmQty = Number(form.medio_mayoreo_qty);
    const mmPrice = Number(form.medio_mayoreo_price);
    if (mmQty > 0 && mmPrice > 0) {
      payload.medio_mayoreo_qty = Math.round(mmQty);
      payload.medio_mayoreo_price = mmPrice;
    } else {
      payload.medio_mayoreo_qty = null;
      payload.medio_mayoreo_price = null;
    }
    const mQty = Number(form.mayoreo_qty);
    const mPrice = Number(form.mayoreo_price);
    if (mQty > 0 && mPrice > 0) {
      payload.mayoreo_qty = Math.round(mQty);
      payload.mayoreo_price = mPrice;
    } else {
      payload.mayoreo_qty = null;
      payload.mayoreo_price = null;
    }
    const shipQty = Number(form.shipment_qty);
    const shipPrice = Number(form.shipment_price);
    if (shipQty > 0 && shipPrice > 0) {
      payload.shipment_qty = Math.round(shipQty);
      payload.shipment_price = shipPrice;
    } else {
      payload.shipment_qty = null;
      payload.shipment_price = null;
    }

    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/korean-products/${editing.id}`, payload, { withCredentials: true });
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/korean-products`, payload, { withCredentials: true });
        toast.success('Producto creado');
      }
      setShowDialog(false);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (item) => {
    try {
      await axios.patch(`${API}/korean-products/${item.id}`, { visible: !item.visible }, { withCredentials: true });
      await refresh();
    } catch (e) {
      toast.error('No se pudo cambiar visibilidad');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/korean-products/${item.id}`, { withCredentials: true });
      toast.success('Producto eliminado');
      await refresh();
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  };

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">🥟 Productos Coreanos</h1>
            <p className="text-muted-foreground mt-1">
              Catálogo independiente con precios por bracket (unidad, medio mayoreo, mayoreo).
              Se muestran en <b>/coreanos</b> para clientes.
            </p>
          </div>
          <Button onClick={() => openDialog()} data-testid="add-korean-btn">
            <Plus size={20} weight="bold" className="mr-2" />
            Registrar producto coreano
          </Button>
        </div>

        <CategoryConsistencyWarning items={items} />

        <div className="bg-white border border-border rounded-sm overflow-x-auto">
          {loading ? (
            <p className="p-8 text-center text-slate-500">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="p-12 text-center text-slate-500">
              Aún no hay productos coreanos. Presiona <b>Registrar producto coreano</b> para agregar el primero.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="korean-admin-table">
              <thead className="bg-slate-50 border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-600">Producto</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-600">Unitario</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-600">Medio mayoreo</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-600">Mayoreo</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold uppercase text-slate-600">Visible</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50" data-testid={`korean-row-${s.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {s.image_url ? (
                          <img src={s.image_url} alt={s.name} loading="lazy" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center text-lg">🥟</div>
                        )}
                        <span className="font-medium text-slate-800">{s.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums">{money(s.unit_price)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">
                      {s.medio_mayoreo_qty && s.medio_mayoreo_price
                        ? <span>≥{s.medio_mayoreo_qty} · {money(s.medio_mayoreo_price)}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                      {s.mayoreo_qty && s.mayoreo_price
                        ? <span>≥{s.mayoreo_qty} · {money(s.mayoreo_price)}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleVisible(s)}
                        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${s.visible ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                        title={s.visible ? 'Ocultar' : 'Mostrar'}
                        data-testid={`korean-visible-toggle-${s.id}`}
                      >
                        {s.visible ? <><Eye size={12} /> Visible</> : <><EyeSlash size={12} /> Oculto</>}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" onClick={() => openDialog(s)} data-testid={`edit-korean-${s.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(s)} data-testid={`delete-korean-${s.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto w-[calc(100vw-16px)] sm:w-full">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar producto coreano' : 'Registrar producto coreano'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="korean-form">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="korean-name-input" />
            </div>

            <div>
              <Label htmlFor="category">Categoría (opcional)</Label>
              <Input
                id="category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="Ej. Ramen, Snacks, Bebidas…"
                list="korean-category-list"
                data-testid="korean-category-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">Se agrupará con otros productos de la misma categoría en `/coreanos`.</p>
            </div>

            <div>
              <Label>Imagen</Label>
              <div className="flex items-center gap-2 mt-1">
                {form.image_url && (
                  <img src={form.image_url} alt="preview" className="w-16 h-16 object-cover rounded border border-slate-200" />
                )}
                <input type="file" accept="image/*" onChange={handleImageChange} disabled={uploadingImage} className="text-xs" data-testid="korean-image-input" />
                {uploadingImage && <span className="text-xs text-slate-500">Subiendo…</span>}
              </div>
            </div>

            <div>
              <Label htmlFor="product_info">Información (opcional)</Label>
              <textarea
                id="product_info"
                rows={2}
                value={form.product_info}
                onChange={(e) => setForm({ ...form, product_info: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                placeholder="Sabor, contenido, ingredientes…"
              />
            </div>

            <div>
              <Label htmlFor="ml_url">Link de Mercado Libre (opcional)</Label>
              <Input
                id="ml_url"
                type="url"
                value={form.ml_url}
                onChange={(e) => setForm({ ...form, ml_url: e.target.value })}
                placeholder="https://articulo.mercadolibre.com.mx/…"
                data-testid="korean-ml-url-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Si lo llenas, el botón de este producto llevará directo a su ficha en ML.
              </p>
            </div>

            <div>
              <Label htmlFor="unit_price">Precio unitario * (desde 1 pieza)</Label>
              <Input
                id="unit_price"
                type="number"
                step="0.01"
                min="0"
                value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                required
                data-testid="korean-unit-price-input"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-amber-800">🟠 Medio mayoreo (opcional)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Desde (piezas)</Label>
                  <Input type="number" min="1" value={form.medio_mayoreo_qty} onChange={(e) => setForm({ ...form, medio_mayoreo_qty: e.target.value })} data-testid="korean-medio-qty-input" />
                </div>
                <div>
                  <Label className="text-xs">Precio unitario</Label>
                  <Input type="number" step="0.01" min="0" value={form.medio_mayoreo_price} onChange={(e) => setForm({ ...form, medio_mayoreo_price: e.target.value })} data-testid="korean-medio-price-input" />
                </div>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-emerald-800">🟢 Mayoreo (opcional)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Desde (piezas)</Label>
                  <Input type="number" min="1" value={form.mayoreo_qty} onChange={(e) => setForm({ ...form, mayoreo_qty: e.target.value })} data-testid="korean-mayoreo-qty-input" />
                </div>
                <div>
                  <Label className="text-xs">Precio unitario</Label>
                  <Input type="number" step="0.01" min="0" value={form.mayoreo_price} onChange={(e) => setForm({ ...form, mayoreo_price: e.target.value })} data-testid="korean-mayoreo-price-input" />
                </div>
              </div>
            </div>

            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-sky-800">📦 Precio en Envío (opcional)</p>
              <p className="text-[11px] text-sky-700">Se muestra sólo en <code>/coreanosenvios</code> para clientes que pagan envío.</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Desde (piezas)</Label>
                  <Input type="number" min="1" value={form.shipment_qty} onChange={(e) => setForm({ ...form, shipment_qty: e.target.value })} data-testid="korean-shipment-qty-input" placeholder="Ej: 6" />
                </div>
                <div>
                  <Label className="text-xs">Precio unitario</Label>
                  <Input type="number" step="0.01" min="0" value={form.shipment_price} onChange={(e) => setForm({ ...form, shipment_price: e.target.value })} data-testid="korean-shipment-price-input" placeholder="Ej: 25.00" />
                </div>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.visible}
                onChange={(e) => setForm({ ...form, visible: e.target.checked })}
                data-testid="korean-visible-input"
              />
              <span className="text-sm">Visible en `/coreanos`</span>
            </label>

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="korean-save-btn">
                {saving ? 'Guardando…' : (editing ? 'Guardar cambios' : 'Registrar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
